/**
 * TeamSection — admin-only team management (Vague 99).
 *
 * Features:
 *  - List members + pending invites with search/filter (name/email/role).
 *  - Per-member action menu (3-dot) with:
 *      - Edit profile (name + email)
 *      - Reset password (email link / temporary password)
 *      - Force logout
 *      - Suspend / Reactivate
 *      - View activity history
 *      - View active sessions
 *      - Send test email
 *      - Revoke access
 *  - Pending invites: resend / cancel.
 *  - Export members CSV.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Trash2, Mail, X, XCircle, Clock, Search, MoreVertical, KeyRound, LogOut,
  PauseCircle, PlayCircle, History, UserCog, RefreshCw, Monitor, Download, Copy, Check, Loader2, ShieldAlert, Unlock,
} from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';
import FormDialog from '../../components/FormDialog';
import ConfirmDialog from '../../components/ConfirmDialog';

const ROLE_STYLES = {
  'Administrateur': 'bg-[#FEE2E2] text-[#B91C1C]',
  'Formateur': 'bg-[#E0E7FF] text-[#3730A3]',
  'Commercial': 'bg-[#FFEDD5] text-[#9A3412]',
  'Lecture seule': 'bg-[#F3F4F8] text-[#6B6C85]',
};

const ROLES = ['Administrateur', 'Formateur', 'Commercial', 'Lecture seule'];

export default function TeamSection() {
  const [invite, setInvite] = useState(false);
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [editProfile, setEditProfile] = useState(null);
  const [resetPwd, setResetPwd] = useState(null); // { member } — opens modal
  const [tempPwdResult, setTempPwdResult] = useState(null); // { member, password, issued_at }
  const [historyMember, setHistoryMember] = useState(null);
  const [sessionsMember, setSessionsMember] = useState(null);
  const [confirmSuspend, setConfirmSuspend] = useState(null);
  const [confirmForceLogout, setConfirmForceLogout] = useState(null);

  const fetchTeam = async () => {
    try {
      const { data } = await api.get('/users/team');
      setMembers(data.members || []);
      setPending(data.pending_invites || []);
      setCurrentUserId(data.current_user_id);
    } catch {
      // non-admin: silently keep empty
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchTeam(); }, []);

  // ----- mutations -----
  const handleInvite = async (values) => {
    try {
      const { data } = await api.post('/users/invite', values);
      if (data.delivered) {
        toast({ title: 'Invitation envoyée ✅', description: `Un email a été délivré à ${values.email}.` });
      } else {
        toast({
          title: '⚠️ Email NON délivré',
          description: `L'invitation est créée mais l'email à ${values.email} n'a pas pu être envoyé. Configurez Resend/SMTP dans Paramètres → Intégrations, ou communiquez le lien d'invitation manuellement.`,
        });
      }
      await fetchTeam();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Invitation impossible' });
    }
  };

  const changeRole = async (memberId, role) => {
    try {
      await api.put(`/users/${memberId}/role`, { role });
      setMembers((m) => m.map((u) => u.user_id === memberId ? { ...u, role } : u));
      toast({ title: 'Rôle mis à jour' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Changement impossible' });
    }
  };

  // Vague 113 L6 — Toggle the "Aussi formateur de l'équipe" hat.
  // Optimistic UI: update local state immediately, rollback on error.
  const toggleTeamTrainer = async (memberId, nextValue) => {
    const prev = members.find((u) => u.user_id === memberId)?.is_team_trainer;
    setMembers((m) => m.map((u) => u.user_id === memberId ? { ...u, is_team_trainer: nextValue } : u));
    try {
      if (nextValue) await api.post(`/users/${memberId}/team-trainer`);
      else await api.delete(`/users/${memberId}/team-trainer`);
      toast({ title: nextValue ? 'Formateur ajouté à l\'équipe' : 'Casquette formateur retirée' });
    } catch (err) {
      // Rollback
      setMembers((m) => m.map((u) => u.user_id === memberId ? { ...u, is_team_trainer: prev } : u));
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const revokeMember = async () => {
    if (!confirmRevoke) return;
    try {
      await api.delete(`/users/${confirmRevoke.user_id}`);
      setMembers((m) => m.filter((u) => u.user_id !== confirmRevoke.user_id));
      toast({ title: 'Accès révoqué', description: `${confirmRevoke.email} ne peut plus se connecter.` });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Révocation impossible' });
    } finally {
      setConfirmRevoke(null);
    }
  };

  const cancelInvite = async () => {
    if (!confirmCancel) return;
    try {
      await api.delete(`/users/invites/by-email/${encodeURIComponent(confirmCancel.email)}`);
      setPending((p) => p.filter((i) => i.email !== confirmCancel.email));
      toast({ title: 'Invitation annulée' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Annulation impossible' });
    } finally {
      setConfirmCancel(null);
    }
  };

  const resendInvite = async (inv) => {
    try {
      const { data } = await api.post(`/users/invites/by-email/${encodeURIComponent(inv.email)}/resend`);
      if (data.delivered) {
        toast({ title: 'Invitation renvoyée ✅', description: `Un nouveau lien a été envoyé à ${inv.email}.` });
      } else {
        toast({
          title: '⚠️ Email NON délivré',
          description: `Le lien d'invitation a été régénéré mais l'envoi à ${inv.email} a échoué. Vérifiez la configuration Resend/SMTP de votre OF.`,
        });
      }
      await fetchTeam();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Renvoi impossible' });
    }
  };

  const sendResetEmail = async (member) => {
    try {
      const { data } = await api.post(`/users/${member.user_id}/password-reset/email`);
      setResetPwd(null);
      if (data.delivered) {
        toast({ title: 'Email envoyé ✅', description: `${member.email} a reçu un lien de réinitialisation valable ${data.expires_in_minutes} min.` });
      } else {
        toast({
          title: '⚠️ Email NON délivré',
          description: `Le lien a été généré mais l'envoi à ${member.email} a échoué (Resend/SMTP non configuré ou erreur). Préférez le mot de passe temporaire pour ce membre.`,
        });
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const generateTempPwd = async (member) => {
    try {
      const { data } = await api.post(`/users/${member.user_id}/password-reset/temporary`);
      setResetPwd(null);
      setTempPwdResult({ member, password: data.temporary_password, issued_at: data.issued_at });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const forceLogout = async () => {
    if (!confirmForceLogout) return;
    try {
      const { data } = await api.post(`/users/${confirmForceLogout.user_id}/force-logout`);
      toast({ title: 'Sessions révoquées', description: `${data.sessions_revoked} session(s) fermée(s) pour ${confirmForceLogout.email}` });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally {
      setConfirmForceLogout(null);
    }
  };

  const toggleSuspend = async () => {
    if (!confirmSuspend) return;
    const { member, action } = confirmSuspend;
    try {
      await api.post(`/users/${member.user_id}/${action}`);
      setMembers((m) => m.map((u) => u.user_id === member.user_id ? { ...u, suspended: action === 'suspend' } : u));
      toast({ title: action === 'suspend' ? 'Membre suspendu' : 'Membre réactivé', description: member.email });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally {
      setConfirmSuspend(null);
    }
  };

  const unlockMember = async (member) => {
    try {
      const { data } = await api.post(`/users/${member.user_id}/unlock`);
      setMembers((arr) => arr.map((u) => u.user_id === member.user_id
        ? { ...u, lockout: { is_locked: false, failed_attempts: 0, attempt_ips: 0, locked_until: null } }
        : u));
      toast({
        title: 'Compte déverrouillé ✅',
        description: data.entries_cleared > 0
          ? `${data.entries_cleared} tentative(s) supprimée(s) pour ${member.email}`
          : `Aucun verrouillage actif sur ${member.email}`,
      });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const updateProfile = async (values) => {
    if (!editProfile) return;
    try {
      const { data } = await api.put(`/users/${editProfile.user_id}/profile`, values);
      setMembers((m) => m.map((u) => u.user_id === editProfile.user_id ? { ...u, ...data } : u));
      toast({ title: 'Profil mis à jour' });
      setEditProfile(null);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const sendTestEmail = async (member) => {
    try {
      const { data } = await api.post(`/users/${member.user_id}/test-email`);
      toast({ title: data.delivered ? 'Email de test envoyé ✅' : 'Email non délivré ⚠️', description: data.to });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const exportCSV = () => {
    const rows = [
      ['Nom', 'Email', 'Rôle', 'Statut', 'Créé le', 'Dernière connexion'],
      ...members.map((m) => [
        m.name || '',
        m.email,
        m.role,
        m.suspended ? 'Suspendu' : 'Actif',
        m.created_at ? new Date(m.created_at).toLocaleString('fr-FR') : '',
        m.last_login ? new Date(m.last_login).toLocaleString('fr-FR') : 'Jamais',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `equipe_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ----- derived: filtered members -----
  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (roleFilter !== 'all' && m.role !== roleFilter) return false;
      if (!q) return true;
      return (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
    });
  }, [members, search, roleFilter]);

  return (
    <div className="space-y-4">
      <div className="soft-card p-6" data-testid="team-members-card">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h3 className="text-[17px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Équipe</h3>
            <p className="text-[13px] text-[#8E8FA5]">{members.length} membre{members.length > 1 ? 's' : ''} {members.length !== filteredMembers.length && `· ${filteredMembers.length} affichés`}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="team-export-csv"
              onClick={exportCSV}
              className="h-10 px-3 rounded-xl bg-white border border-[#EFF0F6] text-[#4A4B63] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#F6F7FB] transition-colors"
              title="Exporter en CSV"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button
              data-testid="team-invite-btn"
              onClick={() => setInvite(true)}
              className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-1.5 hover:opacity-95 transition-opacity"
            >
              <Plus className="w-4 h-4" /> Inviter
            </button>
          </div>
        </div>

        {/* Search + role filter */}
        {members.length > 0 && (
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 text-[#8E8FA5] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="team-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom ou email…"
                className="w-full h-9 pl-9 pr-3 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none"
              />
            </div>
            <select
              data-testid="team-role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-9 px-2.5 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12px] font-semibold cursor-pointer"
            >
              <option value="all">Tous les rôles</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}

        <div className="space-y-2">
          {loading && <div className="text-[12.5px] text-[#8E8FA5] italic">Chargement…</div>}
          {!loading && filteredMembers.length === 0 && (
            <div className="text-[13px] text-[#8E8FA5] italic py-4 text-center">
              {members.length === 0
                ? "Vous n'avez pas accès à la liste des membres de votre organisation."
                : 'Aucun membre ne correspond à votre recherche.'}
            </div>
          )}
          {filteredMembers.map((m) => (
            <MemberRow
              key={m.user_id}
              member={m}
              isSelf={m.user_id === currentUserId}
              onChangeRole={changeRole}
              onToggleTeamTrainer={toggleTeamTrainer}
              onEditProfile={() => setEditProfile(m)}
              onResetPwd={() => setResetPwd(m)}
              onForceLogout={() => setConfirmForceLogout(m)}
              onToggleSuspend={() => setConfirmSuspend({ member: m, action: m.suspended ? 'reactivate' : 'suspend' })}
              onShowHistory={() => setHistoryMember(m)}
              onShowSessions={() => setSessionsMember(m)}
              onTestEmail={() => sendTestEmail(m)}
              onRevoke={() => setConfirmRevoke(m)}
              onUnlock={() => unlockMember(m)}
            />
          ))}
        </div>
      </div>

      {/* Pending invites */}
      {pending.length > 0 && (
        <div className="soft-card p-6" data-testid="team-pending-card">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-[#F59E0B]" />
            <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Invitations en attente
            </h3>
            <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]">{pending.length}</span>
          </div>
          <div className="space-y-2">
            {pending.map((inv) => (
              <div key={inv.email} data-testid={`pending-${inv.email}`} className="flex items-center gap-3 p-3 rounded-xl border border-[#FEF3C7] bg-[#FFFBEB]">
                <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] text-[#92400E] flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{inv.email}</div>
                  <div className="text-[11.5px] text-[#8E8FA5]">
                    Invité par {inv.invited_by_name || inv.invited_by_email} · expire {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString('fr-FR') : ''}
                  </div>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${ROLE_STYLES[inv.role]}`}>{inv.role}</span>
                <button
                  data-testid={`resend-invite-${inv.email}`}
                  onClick={() => resendInvite(inv)}
                  title="Renvoyer l'invitation"
                  className="w-8 h-8 rounded-lg text-[#4F46E5] hover:bg-[#EEF2FF] flex items-center justify-center transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                <button
                  data-testid={`cancel-invite-${inv.email}`}
                  onClick={() => setConfirmCancel(inv)}
                  title="Annuler l'invitation"
                  className="w-8 h-8 rounded-lg text-[#EF4444] hover:bg-[#FEF2F2] flex items-center justify-center transition-colors"
                >
                  <XCircle className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite dialog */}
      <FormDialog
        open={invite} onOpenChange={setInvite}
        title="Inviter un membre"
        submitLabel="Envoyer l'invitation"
        onSubmit={handleInvite}
        fields={[
          { name: 'name', label: 'Nom (optionnel)', full: true },
          { name: 'email', label: 'Email', type: 'email', required: true, full: true },
          { name: 'role', label: 'Rôle', type: 'select', options: ROLES, required: true, full: true },
        ]}
        initialValues={{ role: 'Formateur' }}
      />

      {/* Edit profile dialog */}
      {editProfile && (
        <FormDialog
          open={!!editProfile} onOpenChange={(o) => !o && setEditProfile(null)}
          title={`Modifier le profil — ${editProfile.email}`}
          submitLabel="Enregistrer"
          onSubmit={updateProfile}
          fields={[
            { name: 'name', label: 'Nom complet', full: true },
            { name: 'email', label: 'Email', type: 'email', full: true },
          ]}
          initialValues={{ name: editProfile.name || '', email: editProfile.email }}
        />
      )}

      {/* Confirms */}
      <ConfirmDialog
        open={!!confirmRevoke}
        onOpenChange={(o) => !o && setConfirmRevoke(null)}
        title="Révoquer l'accès ?"
        description={confirmRevoke ? `${confirmRevoke.email} ne pourra plus se connecter. Cette action supprime définitivement le compte (réversible via une nouvelle invitation).` : ''}
        confirmLabel="Révoquer"
        onConfirm={revokeMember}
      />
      <ConfirmDialog
        open={!!confirmCancel}
        onOpenChange={(o) => !o && setConfirmCancel(null)}
        title="Annuler l'invitation ?"
        description={confirmCancel ? `Le lien envoyé à ${confirmCancel.email} sera invalidé.` : ''}
        confirmLabel="Annuler l'invitation"
        onConfirm={cancelInvite}
      />
      <ConfirmDialog
        open={!!confirmForceLogout}
        onOpenChange={(o) => !o && setConfirmForceLogout(null)}
        title="Forcer la déconnexion ?"
        description={confirmForceLogout ? `Toutes les sessions actives de ${confirmForceLogout.email} seront immédiatement fermées. Le mot de passe reste valable.` : ''}
        confirmLabel="Forcer la déconnexion"
        onConfirm={forceLogout}
      />
      <ConfirmDialog
        open={!!confirmSuspend}
        onOpenChange={(o) => !o && setConfirmSuspend(null)}
        title={confirmSuspend?.action === 'suspend' ? 'Suspendre ce membre ?' : 'Réactiver ce membre ?'}
        description={confirmSuspend
          ? (confirmSuspend.action === 'suspend'
              ? `${confirmSuspend.member.email} ne pourra plus se connecter, mais ses données (audits, devis, factures) sont conservées. Réversible à tout moment.`
              : `${confirmSuspend.member.email} pourra se reconnecter normalement.`)
          : ''}
        confirmLabel={confirmSuspend?.action === 'suspend' ? 'Suspendre' : 'Réactiver'}
        onConfirm={toggleSuspend}
      />

      {/* Reset password modal */}
      {resetPwd && (
        <ResetPasswordModal
          member={resetPwd}
          onClose={() => setResetPwd(null)}
          onEmail={() => sendResetEmail(resetPwd)}
          onTemp={() => generateTempPwd(resetPwd)}
        />
      )}

      {/* Temp password result modal */}
      {tempPwdResult && (
        <TempPasswordResultModal
          result={tempPwdResult}
          onClose={() => setTempPwdResult(null)}
        />
      )}

      {/* History drawer */}
      {historyMember && (
        <UserHistoryDrawer
          member={historyMember}
          onClose={() => setHistoryMember(null)}
        />
      )}

      {/* Active sessions drawer */}
      {sessionsMember && (
        <UserSessionsDrawer
          member={sessionsMember}
          onClose={() => setSessionsMember(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// MemberRow — single member with action menu
// =============================================================================
function MemberRow({ member, isSelf, onChangeRole, onToggleTeamTrainer, onEditProfile, onResetPwd, onForceLogout, onToggleSuspend, onShowHistory, onShowSessions, onTestEmail, onRevoke, onUnlock }) {
  const [menu, setMenu] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menu]);

  const m = member;
  return (
    <div data-testid={`team-member-${m.user_id}`} className={`flex items-center gap-3 p-3 rounded-xl border ${m.suspended ? 'bg-[#FEF3C7] border-[#FCD34D]' : 'border-[#F3F4F8] hover:bg-[#FAFAFE]'} transition-colors`}>
      <img
        src={m.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name || m.email)}&background=7C5CFC&color=fff`}
        alt=""
        className={`w-10 h-10 rounded-lg object-cover ${m.suspended ? 'grayscale opacity-60' : ''}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-[#1A1B2E] truncate flex items-center gap-1.5">
          {m.name || m.email.split('@')[0]}
          {isSelf && <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5]">Vous</span>}
          {m.suspended && (
            <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C] inline-flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" /> Suspendu
            </span>
          )}
          {m.must_change_password && !m.suspended && (
            <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]">MdP à changer</span>
          )}
          {(m.multi_org_count || 1) > 1 && (
            <span
              className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#E6FBF7] text-[#0F766E]"
              title={`Cet email est rattaché à ${m.multi_org_count} organismes`}
              data-testid={`member-multiorg-${m.user_id}`}
            >
              {m.multi_org_count} organismes
            </span>
          )}
          {m.lockout?.is_locked && (
            <span
              className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C] inline-flex items-center gap-1"
              title={m.lockout.locked_until ? `Verrouillé jusqu'à ${new Date(m.lockout.locked_until).toLocaleString('fr-FR')}` : 'Verrouillé suite à trop de tentatives échouées'}
              data-testid={`member-locked-${m.user_id}`}
            >
              <ShieldAlert className="w-3 h-3" /> Verrouillé
            </span>
          )}
          {!m.lockout?.is_locked && (m.lockout?.failed_attempts || 0) > 0 && (
            <span
              className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#FFF7E6] text-[#92400E]"
              title={`${m.lockout.failed_attempts} tentative(s) échouée(s) récentes`}
              data-testid={`member-fails-${m.user_id}`}
            >
              {m.lockout.failed_attempts} tentative{m.lockout.failed_attempts > 1 ? 's' : ''} échouée{m.lockout.failed_attempts > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="text-[12px] text-[#8E8FA5] truncate flex items-center gap-2">
          <span>{m.email}</span>
          {m.last_login && <span className="text-[10.5px] text-[#B8B9CC]">· dernière connexion {new Date(m.last_login).toLocaleDateString('fr-FR')}</span>}
        </div>
      </div>
      <select
        data-testid={`role-select-${m.user_id}`}
        value={m.role}
        onChange={(e) => onChangeRole(m.user_id, e.target.value)}
        disabled={isSelf}
        className={`h-8 px-2 rounded-md border-0 text-[11.5px] font-semibold outline-none cursor-pointer disabled:cursor-not-allowed ${ROLE_STYLES[m.role] || 'bg-[#F3F4F8] text-[#6B6C85]'}`}
      >
        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      {/* Vague 113 L6 — "Aussi formateur de l'équipe" hat.
          Hidden when the user's primary role is already "Formateur"
          (he/she is a freelance trainer — the hat doesn't apply). */}
      {m.role !== 'Formateur' && (
        <label
          className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium text-[#4A4B63] cursor-pointer hover:bg-[#F6F7FB] select-none"
          title="Cocher pour donner accès à l'espace formateur en plus du rôle administratif"
          data-testid={`team-trainer-toggle-${m.user_id}`}
        >
          <input
            type="checkbox"
            checked={!!m.is_team_trainer}
            onChange={(e) => onToggleTeamTrainer(m.user_id, e.target.checked)}
            className="w-3.5 h-3.5 rounded accent-[#4F46E5]"
            data-testid={`team-trainer-checkbox-${m.user_id}`}
          />
          <span className="hidden sm:inline">Aussi formateur</span>
          <span className="sm:hidden">+Form.</span>
        </label>
      )}
      {!isSelf && (
        <div className="relative" ref={ref}>
          <button
            data-testid={`actions-menu-${m.user_id}`}
            onClick={() => setMenu((v) => !v)}
            className="w-8 h-8 rounded-lg text-[#6B6C85] hover:bg-[#F3F4F8] flex items-center justify-center transition-colors"
            title="Actions"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {menu && (
            <div className="absolute right-0 top-9 z-30 w-60 py-1 bg-white border border-[#EFF0F6] rounded-xl shadow-xl" data-testid={`actions-menu-popover-${m.user_id}`}>
              <MenuItem icon={UserCog} label="Modifier nom/email" onClick={() => { setMenu(false); onEditProfile(); }} testid={`menu-edit-profile-${m.user_id}`} />
              <MenuItem icon={KeyRound} label="Réinitialiser mot de passe" onClick={() => { setMenu(false); onResetPwd(); }} testid={`menu-reset-${m.user_id}`} />
              {(m.lockout?.is_locked || (m.lockout?.failed_attempts || 0) > 0) && (
                <MenuItem icon={Unlock} label="Déverrouiller le compte" onClick={() => { setMenu(false); onUnlock?.(); }} testid={`menu-unlock-${m.user_id}`} />
              )}
              <MenuItem icon={LogOut} label="Forcer la déconnexion" onClick={() => { setMenu(false); onForceLogout(); }} testid={`menu-force-logout-${m.user_id}`} />
              <MenuDivider />
              {m.suspended ? (
                <MenuItem icon={PlayCircle} label="Réactiver" onClick={() => { setMenu(false); onToggleSuspend(); }} testid={`menu-reactivate-${m.user_id}`} />
              ) : (
                <MenuItem icon={PauseCircle} label="Suspendre" onClick={() => { setMenu(false); onToggleSuspend(); }} testid={`menu-suspend-${m.user_id}`} />
              )}
              <MenuDivider />
              <MenuItem icon={History} label="Voir l'activité" onClick={() => { setMenu(false); onShowHistory(); }} testid={`menu-history-${m.user_id}`} />
              <MenuItem icon={Monitor} label="Sessions actives" onClick={() => { setMenu(false); onShowSessions(); }} testid={`menu-sessions-${m.user_id}`} />
              <MenuItem icon={Mail} label="Envoyer un email de test" onClick={() => { setMenu(false); onTestEmail(); }} testid={`menu-test-email-${m.user_id}`} />
              <MenuDivider />
              <MenuItem icon={Trash2} label="Révoquer l'accès" onClick={() => { setMenu(false); onRevoke(); }} danger testid={`menu-revoke-${m.user_id}`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger = false, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full px-3 py-2 text-left text-[12.5px] font-medium flex items-center gap-2 transition-colors ${danger ? 'text-[#B91C1C] hover:bg-[#FEF2F2]' : 'text-[#1A1B2E] hover:bg-[#F6F7FB]'}`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}
function MenuDivider() {
  return <div className="my-1 h-px bg-[#EFF0F6]" />;
}

// =============================================================================
// Modals & Drawers
// =============================================================================
function ResetPasswordModal({ member, onClose, onEmail, onTemp }) {
  return (
    <Modal onClose={onClose} title={`Réinitialiser le mot de passe — ${member.email}`} testid="reset-pwd-modal">
      <p className="text-[13px] text-[#4A4B63] mb-3">Choisissez la méthode de réinitialisation :</p>
      <div className="text-[11.5px] text-[#92400E] bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-3 py-2 mb-4">
        💡 Si votre OF n'a pas encore configuré Resend/SMTP, l'email ne sera pas délivré.
        Préférez alors le <strong>mot de passe temporaire</strong> et communiquez-le par un autre canal.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          data-testid="reset-pwd-by-email"
          onClick={onEmail}
          className="p-4 rounded-xl bg-[#EEF2FF] border border-[#E0DAFF] text-left hover:border-[#4F46E5] transition-colors"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <Mail className="w-4 h-4 text-[#4F46E5]" />
            <span className="text-[13px] font-semibold text-[#1A1B2E]">Envoyer un email</span>
          </div>
          <p className="text-[11.5px] text-[#6B6C85] leading-relaxed">
            Le membre reçoit un lien (60 min) et choisit lui-même son nouveau mot de passe. <strong>Recommandé.</strong>
          </p>
        </button>
        <button
          data-testid="reset-pwd-by-temporary"
          onClick={onTemp}
          className="p-4 rounded-xl bg-white border border-[#EFF0F6] text-left hover:border-[#4F46E5] transition-colors"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <KeyRound className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-[13px] font-semibold text-[#1A1B2E]">Mot de passe temporaire</span>
          </div>
          <p className="text-[11.5px] text-[#6B6C85] leading-relaxed">
            Génère un MdP unique à transmettre au membre. Sa première connexion lui demandera de le changer. Toutes ses sessions sont fermées.
          </p>
        </button>
      </div>
    </Modal>
  );
}

function TempPasswordResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard API blocked or denied — user can copy manually */ console.warn("[clipboard]", e); }
  };
  return (
    <Modal onClose={onClose} title="Mot de passe temporaire généré" testid="temp-pwd-result-modal">
      <div className="p-3 rounded-lg bg-[#FEF3C7] border border-[#FCD34D] mb-3">
        <p className="text-[12px] text-[#92400E] font-semibold mb-1">⚠️ Affiché une seule fois</p>
        <p className="text-[11.5px] text-[#92400E]">Communiquez ce mot de passe à <strong>{result.member.email}</strong> via un canal sécurisé. Toutes ses sessions ont été fermées.</p>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <input
          data-testid="temp-pwd-value"
          readOnly
          value={result.password}
          onClick={(e) => e.target.select()}
          className="flex-1 h-11 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[15px] font-mono text-[#1A1B2E]"
        />
        <button
          data-testid="temp-pwd-copy"
          onClick={copy}
          className={`h-11 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1 transition-colors ${copied ? 'bg-[#E6FBF7] text-[#0F766E]' : 'bg-white border border-[#EFF0F6] text-[#4F46E5] hover:bg-[#EEF2FF]'}`}
        >
          {copied ? <><Check className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier</>}
        </button>
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={onClose} className="h-9 px-4 rounded-lg brand-gradient text-white text-[12.5px] font-semibold">Fermer</button>
      </div>
    </Modal>
  );
}

function UserHistoryDrawer({ member, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/users/${member.user_id}/audit?limit=50`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, [member.user_id]);
  return (
    <Drawer onClose={onClose} title={`Activité — ${member.name || member.email}`} testid="user-history-drawer">
      {items === null && <div className="text-[12.5px] text-[#8E8FA5] italic flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</div>}
      {Array.isArray(items) && items.length === 0 && <div className="text-[12.5px] text-[#8E8FA5] italic">Aucune activité enregistrée.</div>}
      {Array.isArray(items) && items.length > 0 && (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="p-2.5 rounded-lg border border-[#EFF0F6] bg-white">
              <div className="text-[12.5px] font-semibold text-[#1A1B2E]">{it.action}</div>
              <div className="text-[11.5px] text-[#8E8FA5]">
                {it.resource_type} · {it.resource_id}
                {it.created_at && <> · {new Date(it.created_at).toLocaleString('fr-FR')}</>}
              </div>
              {it.metadata && Object.keys(it.metadata).length > 0 && (
                <details className="mt-1 text-[11px] text-[#6B6C85]">
                  <summary className="cursor-pointer hover:text-[#4F46E5]">Détails</summary>
                  <pre className="mt-1 p-2 rounded bg-[#F6F7FB] overflow-x-auto">{JSON.stringify(it.metadata, null, 2)}</pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}

function UserSessionsDrawer({ member, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/users/${member.user_id}/sessions`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, [member.user_id]);
  return (
    <Drawer onClose={onClose} title={`Sessions actives — ${member.name || member.email}`} testid="user-sessions-drawer">
      {items === null && <div className="text-[12.5px] text-[#8E8FA5] italic flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</div>}
      {Array.isArray(items) && items.length === 0 && <div className="text-[12.5px] text-[#8E8FA5] italic">Aucune session active.</div>}
      {Array.isArray(items) && items.length > 0 && (
        <div className="space-y-2">
          {items.map((s, i) => (
            <div key={i} className="p-2.5 rounded-lg border border-[#EFF0F6] bg-white">
              <div className="text-[12.5px] font-semibold text-[#1A1B2E] flex items-center gap-1.5">
                <Monitor className="w-3.5 h-3.5 text-[#4F46E5]" />
                {s.user_agent || 'Navigateur inconnu'}
              </div>
              <div className="text-[11.5px] text-[#8E8FA5] mt-0.5">
                IP : {s.ip || '—'}
                {s.created_at && <> · ouverte {new Date(s.created_at).toLocaleString('fr-FR')}</>}
                {s.expires_at && <> · expire {new Date(s.expires_at).toLocaleString('fr-FR')}</>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}

// Lightweight modal & drawer primitives (kept inline for self-contained section)
function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#F3F4F8] flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Drawer({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl overflow-y-auto">
        <div className="p-5 border-b border-[#EFF0F6] flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#F3F4F8] flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
